import React, { useState } from 'react';
import { ConfirmationModal } from './ConfirmationModal';
import { PlayerPhoto } from './PlayerPhoto';
import type { Player } from '@/types/player';

interface PositionGroupProps {
  position: string;
  players: Player[];
  onAddPlayer: (position: string) => void;
  onRemovePlayer: (playerId: string) => void;
  onToggleStarter?: (playerId: string, isStarter: boolean) => void;
  starterPlayerIds?: string[];
}

export const PositionGroup: React.FC<PositionGroupProps> = ({
  position,
  players,
  onAddPlayer,
  onRemovePlayer,
  onToggleStarter = () => {},
  starterPlayerIds = []
}) => {
  const displayPosition = position === 'DEF' ? 'D/ST' : position;
  const [showBench, setShowBench] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ isOpen: boolean; player: Player | null }>({
    isOpen: false,
    player: null
  });

  const starters = players.filter(p => starterPlayerIds.includes(String(p.id)));
  const benchPlayers = players.filter(p => !starterPlayerIds.includes(String(p.id)));
  const showAllAsList = starters.length === 0 && benchPlayers.length === 0 && players.length > 0;

  const handleToggleStarter = (player: Player) => {
    const isCurrentlyStarter = starterPlayerIds.includes(String(player.id));
    onToggleStarter(String(player.id), !isCurrentlyStarter);
  };

  const handleConfirmRemove = () => {
    if (confirmRemove.player) {
      onRemovePlayer(String(confirmRemove.player.id));
      setConfirmRemove({ isOpen: false, player: null });
    }
  };

  const renderPlayerList = (playerList: Player[], isStarters: boolean) => (
    <ul>
      {playerList.map((player) => (
        <li key={player.id}>
          <div className="group flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-white/5">
            <PlayerPhoto
              name={player.name}
              position={player.position}
              team={player.team}
              sleeperId={player.id}
              espnId={player.espn_id}
              imageUrl={player.imageUrl}
              alt={player.name}
              className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className={`truncate text-sm ${isStarters ? 'text-white' : 'text-zinc-300'}`}>
                {player.name}
              </p>
              <p className="text-xs text-zinc-300">
                {player.team} · {displayPosition}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleToggleStarter(player)}
              className={`text-xs ${
                isStarters ? 'text-fiesta-yellow' : 'text-zinc-400 hover:text-white'
              }`}
            >
              {isStarters ? 'Starter' : 'Bench'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmRemove({ isOpen: true, player })}
              className="p-1 text-zinc-400 opacity-80 hover:text-rose-300 group-hover:opacity-100"
              title="Remove player"
              aria-label={`Remove ${player.name}`}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <section className="border-t border-white/5 py-3 first:border-t-0 first:pt-0 last:pb-0">
      <div className="mb-1 flex items-baseline justify-between px-2">
        <h3 className="text-xs font-semibold tracking-wide text-zinc-300">
          {displayPosition}
          <span className="ml-1.5 font-normal text-zinc-400">{players.length}</span>
        </h3>
        <button
          type="button"
          onClick={() => onAddPlayer(position)}
          className="text-xs font-medium text-zinc-300 hover:text-white"
        >
          Add
        </button>
      </div>

      {players.length === 0 ? (
        <button
          type="button"
          onClick={() => onAddPlayer(position)}
          className="w-full rounded-xl px-2 py-3 text-left text-sm text-zinc-400 hover:bg-white/5 hover:text-white"
        >
          Add {displayPosition} players
        </button>
      ) : (
        <>
          {starters.length > 0 ? (
            <div>
              <p className="px-2 pb-0.5 text-[11px] font-medium text-zinc-400">
                Starters ({starters.length})
              </p>
              {renderPlayerList(starters, true)}
            </div>
          ) : null}

          {benchPlayers.length > 0 ? (
            <div>
              <button
                type="button"
                onClick={() => setShowBench(!showBench)}
                className="flex w-full items-center justify-between px-2 py-1 text-left"
              >
                <span className="text-[11px] font-medium text-zinc-400">Bench ({benchPlayers.length})</span>
                <svg
                  className={`h-3 w-3 text-zinc-400 transition-transform ${showBench ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showBench ? renderPlayerList(benchPlayers, false) : null}
            </div>
          ) : null}

          {showAllAsList ? renderPlayerList(players, false) : null}
        </>
      )}

      <ConfirmationModal
        isOpen={confirmRemove.isOpen}
        onClose={() => setConfirmRemove({ isOpen: false, player: null })}
        onConfirm={handleConfirmRemove}
        title="Remove player"
        message={`Remove ${confirmRemove.player?.name || 'this player'} from ${displayPosition}?`}
        confirmText="Remove"
        cancelText="Keep"
        confirmButtonColor="red"
      />
    </section>
  );
};
